import { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { billingApi } from '../api/client';
import { T } from '../components/ui';

const TIER_LABELS: Record<string, { name: string; color: string; badge: string }> = {
  starter:    { name: 'Starter',    color: T.sub,    badge: '🌱' },
  growth:     { name: 'Growth',     color: T.copper, badge: '🚀' },
  enterprise: { name: 'Enterprise', color: T.sage,   badge: '⭐' },
};

function UsageMeter({ label, used, max }: { label: string; used: number; max: number }) {
  const pct = max >= 999999 ? 0 : Math.min(100, Math.round((used / max) * 100));
  const warn = pct > 80;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 13, color: T.text }}>{label}</span>
        <span style={{ fontSize: 12, color: warn ? T.crimson : T.sub }}>
          {used.toLocaleString()} / {max >= 999999 ? '∞' : max.toLocaleString()}
        </span>
      </div>
      {max < 999999 && (
        <div style={{
          height: 6, background: T.border, borderRadius: 3, overflow: 'hidden',
        }}>
          <div style={{
            height: '100%',
            width: `${pct}%`,
            background: warn ? T.crimson : T.copper,
            borderRadius: 3,
            transition: 'width 0.4s',
          }} />
        </div>
      )}
    </div>
  );
}

export default function BillingPage() {
  const { user } = useAuth();
  const { data, loading, error } = useApi(() => billingApi.status(), [user]);
  const [upgrading, setUpgrading] = useState('');
  const [devActivating, setDevActivating] = useState('');

  const upgrade = async (tier: string) => {
    setUpgrading(tier);
    try {
      const { url } = await billingApi.createCheckoutSession(tier);
      window.location.assign(url);
    } catch (e) {
      alert('Failed to start checkout. Please try again.');
    } finally {
      setUpgrading('');
    }
  };

  const manageSubscription = async () => {
    try {
      const { url } = await billingApi.createPortalSession();
      window.location.assign(url);
    } catch (e) {
      alert('Failed to open billing portalApi.');
    }
  };

  const devActivate = async (tier: string) => {
    setDevActivating(tier);
    try {
      await billingApi.devActivateTier(tier);
      window.location.reload();
    } catch (e) {
      alert('Dev activation failed.');
    } finally {
      setDevActivating('');
    }
  };

  if (loading) return (
    <div style={{ color: T.sub, fontSize: 14, paddingTop: 40, textAlign: 'center' }}>
      Loading billing info…
    </div>
  );

  if (error || !data) return (
    <div style={{ color: T.crimson, fontSize: 14 }}>Failed to load billing information.</div>
  );

  const currentTier = data.tier;
  const tierInfo    = TIER_LABELS[currentTier] || TIER_LABELS.starter;

  return (
    <div style={{ maxWidth: 760 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: T.text, marginBottom: 4 }}>
        Billing & Plan
      </h1>
      <p style={{ fontSize: 13, color: T.sub, marginBottom: 32 }}>
        Manage your subscription and monitor usage
      </p>

      {/* Dev mode banner */}
      {data.dev_mode && (
        <div style={{
          background: `${T.copper}15`, border: `1px solid ${T.copper}40`,
          borderRadius: 10, padding: '12px 16px', marginBottom: 24,
          fontSize: 13, color: T.copper,
        }}>
          ⚡ <strong>Dev mode</strong> — Stripe is not configured.{' '}
          Use the tier activation buttons below to test billing flows.
        </div>
      )}

      {/* Current plan card */}
      <div style={{
        background: T.card, border: `1px solid ${T.border}`,
        borderRadius: 12, padding: 24, marginBottom: 24,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 20 }}>{tierInfo.badge}</span>
              <span style={{ fontSize: 18, fontWeight: 700, color: T.text }}>{tierInfo.name}</span>
              <span style={{
                fontSize: 11, padding: '2px 8px',
                background: data.subscription_status === 'active' ? `${T.sage}20` :
                            data.subscription_status === 'trialing' ? `${T.copper}20` : `${T.crimson}20`,
                color: data.subscription_status === 'active' ? T.sage :
                       data.subscription_status === 'trialing' ? T.copper : T.crimson,
                borderRadius: 20, fontWeight: 600, textTransform: 'capitalize',
              }}>
                {data.subscription_status}
              </span>
            </div>
            {data.subscription_status === 'trialing' && data.trial_ends_at && (
              <div style={{ fontSize: 12, color: T.sub }}>
                Trial ends {new Date(data.trial_ends_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </div>
            )}
            {data.current_period_end && data.subscription_status === 'active' && (
              <div style={{ fontSize: 12, color: T.sub }}>
                Renews {new Date(data.current_period_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
              </div>
            )}
          </div>

          {data.has_payment_method && (
            <button
              onClick={manageSubscription}
              style={{
                padding: '8px 16px',
                background: 'transparent',
                border: `1px solid ${T.border}`,
                borderRadius: 8, color: T.text,
                fontSize: 13, cursor: 'pointer',
              }}
            >
              Manage subscription
            </button>
          )}
        </div>
      </div>

      {/* Usage meters */}
      <div style={{
        background: T.card, border: `1px solid ${T.border}`,
        borderRadius: 12, padding: 24, marginBottom: 24,
      }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, color: T.text, margin: '0 0 20px' }}>
          Current usage
        </h3>
        <UsageMeter label="Products"       used={data.limits.products.used}  max={data.limits.products.max} />
        <UsageMeter label="Team members"   used={data.limits.users.used}     max={data.limits.users.max} />
        <UsageMeter label="API calls this month" used={data.limits.api_calls.used} max={data.limits.api_calls.max} />
      </div>

      {/* Upgrade options */}
      {currentTier !== 'enterprise' && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: T.text, margin: '0 0 16px' }}>
            {currentTier === 'starter' ? 'Upgrade your plan' : 'Upgrade to Enterprise'}
          </h3>
          <div style={{ display: 'flex', gap: 16 }}>
            {(currentTier === 'starter' ? ['growth', 'enterprise'] : ['enterprise']).map(tier => {
              const t = TIER_LABELS[tier];
              const limits = data.tiers[tier];
              return (
                <div key={tier} style={{
                  flex: 1,
                  background: T.card, border: `1px solid ${T.border}`,
                  borderRadius: 12, padding: 20,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span>{t.badge}</span>
                    <span style={{ fontWeight: 700, color: T.text }}>{t.name}</span>
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: T.text, marginBottom: 12 }}>
                    {limits.price_usd ? `$${limits.price_usd}/mo` : 'Custom'}
                  </div>
                  <div style={{ fontSize: 12, color: T.sub, marginBottom: 16 }}>
                    <div>Up to {limits.max_products >= 999999 ? 'unlimited' : limits.max_products.toLocaleString()} products</div>
                    <div>Up to {limits.max_users >= 999 ? 'unlimited' : limits.max_users} users</div>
                  </div>
                  {data.dev_mode ? (
                    <button
                      onClick={() => devActivate(tier)}
                      disabled={devActivating === tier}
                      style={{
                        width: '100%', padding: '8px',
                        background: T.copper, color: '#fff',
                        border: 'none', borderRadius: 8,
                        fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      {devActivating === tier ? 'Activating…' : `[DEV] Activate ${t.name}`}
                    </button>
                  ) : tier === 'enterprise' ? (
                    <a
                      href="mailto:sales@tracefull.ai"
                      style={{
                        display: 'block', width: '100%', padding: '8px',
                        background: 'transparent', color: T.copper,
                        border: `1px solid ${T.copper}`, borderRadius: 8,
                        fontSize: 13, fontWeight: 600, cursor: 'pointer',
                        textAlign: 'center', textDecoration: 'none', boxSizing: 'border-box',
                      }}
                    >
                      Contact sales
                    </a>
                  ) : (
                    <button
                      onClick={() => upgrade(tier)}
                      disabled={upgrading === tier}
                      style={{
                        width: '100%', padding: '8px',
                        background: T.copper, color: '#fff',
                        border: 'none', borderRadius: 8,
                        fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      {upgrading === tier ? 'Redirecting…' : `Upgrade to ${t.name}`}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
